"use client";

import { usePathname } from "next/navigation";

import { regionFromPathname, type Region } from "@/lib/region";
import { resolveRichTextHref } from "@/lib/storyblok-link";

type RichTextNode = {
  type: string;
  content?: RichTextNode[];
  text?: string;
  marks?: { type: string; attrs?: { href?: string; target?: string } }[];
};

export function FaqRichText({ node }: { node: RichTextNode | undefined }) {
  const region = regionFromPathname(usePathname() ?? "/");
  return <>{renderNode(node, region)}</>;
}

// Avoids markup escaping issues in the FAQPage JSON-LD payload.
export function richTextToPlainText(node: RichTextNode | undefined): string {
  if (!node) return "";
  if (node.type === "text") return node.text ?? "";
  if (node.type === "hard_break") return "\n";

  const children = node.content?.map(richTextToPlainText) ?? [];

  if (node.type === "paragraph") return children.join("");
  if (node.type === "list_item") return children.join("");
  if (node.type === "bullet_list" || node.type === "ordered_list") {
    return children
      .map((c) => c.trim())
      .filter(Boolean)
      .map((c) => `• ${c}`)
      .join("\n");
  }
  return children
    .map((c) => c.trim())
    .filter(Boolean)
    .join("\n\n");
}

function renderNode(
  node: RichTextNode | undefined,
  region?: Region,
): React.ReactNode {
  if (!node) return null;
  if (node.type === "doc") {
    return node.content?.map((n, i) => (
      <span key={i}>{renderNode(n, region)}</span>
    ));
  }
  if (node.type === "paragraph") {
    return (
      <p className="mt-4 text-body leading-relaxed text-black/80">
        {node.content?.map((n, i) => (
          <span key={i}>{renderNode(n, region)}</span>
        ))}
      </p>
    );
  }
  if (node.type === "hard_break") {
    return <br />;
  }
  if (node.type === "text") {
    const text = node.text ?? "";
    let el: React.ReactNode = text;
    for (const mark of node.marks ?? []) {
      if (mark.type === "bold") el = <strong>{el}</strong>;
      else if (mark.type === "italic") el = <em>{el}</em>;
      else if (mark.type === "link" && mark.attrs?.href && (node.text ?? "").trim()) {
        const href = resolveRichTextHref(mark.attrs.href, region);
        const isExternal = /^https?:\/\//i.test(href);
        const target = isExternal ? "_blank" : mark.attrs.target;
        el = (
          <a
            href={href}
            target={target}
            rel={target === "_blank" ? "noopener noreferrer" : undefined}
            className="text-purple-03 underline underline-offset-2 hover:opacity-80"
          >
            {el}
          </a>
        );
      }
    }
    return el;
  }
  if (node.type === "bullet_list") {
    return (
      <ul className="mt-4 ml-5 list-disc space-y-1.5 text-body text-black/80">
        {node.content?.map((n, i) => <li key={i}>{renderNode(n, region)}</li>)}
      </ul>
    );
  }
  if (node.type === "ordered_list") {
    return (
      <ol className="mt-4 ml-5 list-decimal space-y-1.5 text-body text-black/80">
        {node.content?.map((n, i) => <li key={i}>{renderNode(n, region)}</li>)}
      </ol>
    );
  }
  if (node.type === "list_item") {
    return node.content?.map((child, i) => {
      if (child.type === "paragraph") {
        return (
          <span key={i}>
            {child.content?.map((n, j) => (
              <span key={j}>{renderNode(n, region)}</span>
            ))}
          </span>
        );
      }
      return <span key={i}>{renderNode(child, region)}</span>;
    });
  }
  return node.content?.map((n, i) => (
    <span key={i}>{renderNode(n, region)}</span>
  ));
}
